import type { WorkOrderChangePriceData } from 'gql/graphql';
import { AddRatioToActKc2StrategyEnum, WorkOrderPriceStatusEnum, WorkOrderPriceTypeEnum } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';

import { useCallback, useMemo } from 'react';

interface IUseWorkOrderChangeRowMenuItems {
    rowData: WorkOrderChangePriceData;
    openSingleDeleteModalHandler?: () => void;
    checkSingleRowHandler?: ({ id, data }: { id: number; data: AnyObject | null }) => void;
    openAddMtrModalHandler?: () => void;
    openEditMtrModalHandler?: () => void;
    openEditNoteModalHandler?: () => void;
    setRowData?: (data: any) => void;
    openAddRatioModalHandler?: (strategy: AddRatioToActKc2StrategyEnum | null) => void;
    openEditOtherDistributionHandler?: () => void;
    openEditOtherModalHandler?: () => void;
    openViewOtherModalHandler?: () => void;
    openEditRatioModal?: () => void;
    openEditUerModal?: () => void;
    openMoveVolumesBetweenSeasonsForUer?: () => void;
    isOpenMoveVolumesBetweenSeasonsForUerDisabled?: boolean;
    openMatchMtrDialogHandler?: () => void;
}

export const useWorkOrderChangeRowMenuItems = (props: IUseWorkOrderChangeRowMenuItems) => {
    const {
        rowData,
        openSingleDeleteModalHandler,
        checkSingleRowHandler,
        openAddMtrModalHandler,
        openEditMtrModalHandler,
        openEditNoteModalHandler,
        openAddRatioModalHandler,
        openEditOtherDistributionHandler,
        openEditOtherModalHandler,
        openViewOtherModalHandler,
        openEditRatioModal,
        openEditUerModal,
        setRowData,
        openMoveVolumesBetweenSeasonsForUer,
        isOpenMoveVolumesBetweenSeasonsForUerDisabled,
        openMatchMtrDialogHandler,
    } = props;

    const singleDeleteHandler = useCallback(() => {
        openSingleDeleteModalHandler?.();
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
    }, [checkSingleRowHandler, openSingleDeleteModalHandler, rowData]);

    const editUerHandler = useCallback(() => {
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
        openEditUerModal?.();
    }, [checkSingleRowHandler, openEditUerModal, rowData]);

    const addMtrHandler = useCallback(() => {
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
        openAddMtrModalHandler?.();
    }, [checkSingleRowHandler, openAddMtrModalHandler, rowData]);

    const matchMtrDialogHandler = useCallback(() => {
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
        openMatchMtrDialogHandler?.();
    }, [checkSingleRowHandler, openMatchMtrDialogHandler, rowData]);

    const editNoteHandler = useCallback(() => {
        openEditNoteModalHandler?.();
        setRowData?.(rowData);
    }, [openEditNoteModalHandler, rowData, setRowData]);

    const editMtrHandler = useCallback(() => {
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
        openEditMtrModalHandler?.();
    }, [checkSingleRowHandler, openEditMtrModalHandler, rowData]);

    const addRatioHandler = useCallback(
        ({ strategy }: { strategy: AddRatioToActKc2StrategyEnum | null }) => {
            checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
            openAddRatioModalHandler?.(strategy);
        },
        [checkSingleRowHandler, openAddRatioModalHandler, rowData],
    );

    const editRatioHandler = useCallback(() => {
        openEditRatioModal?.();
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
    }, [checkSingleRowHandler, openEditRatioModal, rowData]);

    const editOtherDistributionHandler = useCallback(() => {
        openEditOtherDistributionHandler?.();
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
    }, [checkSingleRowHandler, openEditOtherDistributionHandler, rowData]);

    const editOtherHandler = useCallback(
        (isReadOnly: boolean) => {
            if (isReadOnly) {
                openViewOtherModalHandler?.();
            } else {
                openEditOtherModalHandler?.();
            }

            checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
        },
        [checkSingleRowHandler, openEditOtherModalHandler, openViewOtherModalHandler, rowData],
    );

    const onOpenMoveVolumesBetweenSeasonsForUer = useCallback(() => {
        openMoveVolumesBetweenSeasonsForUer?.();
        checkSingleRowHandler?.({ id: rowData.clientId, data: rowData });
    }, [checkSingleRowHandler, openMoveVolumesBetweenSeasonsForUer, rowData]);

    const { rowHasAcceptedCost, canRemoveRow, canExcludeRow } = useMemo(
        () => ({
            canRemoveRow: rowData.hasAcceptedCost === null,
            canExcludeRow: rowData.hasAcceptedCost === false,
            rowHasAcceptedCost: rowData.hasAcceptedCost,
        }),
        [rowData.hasAcceptedCost],
    );

    const editOperationDisabled = useMemo(
        () =>
            !(
                rowData.isAddedPrice &&
                !rowData.isActKC2Price &&
                (rowData.status === WorkOrderPriceStatusEnum.NewRecord ||
                    rowData.status === WorkOrderPriceStatusEnum.Draft)
            ),
        [rowData.isActKC2Price, rowData.isAddedPrice, rowData.status],
    );

    const isDuplicate = useMemo(() => 'hasDuplicates' in rowData && rowData.hasDuplicates, [rowData]);

    const uerMenu = useMemo(
        () => [
            {
                label: 'Перенести объемы между сезонами',
                groupId: 1,
                action: onOpenMoveVolumesBetweenSeasonsForUer,
                disabled: isOpenMoveVolumesBetweenSeasonsForUerDisabled,
            },
            {
                label: 'Редактировать расценку',
                groupId: 1,
                action: editUerHandler,
                disabled: editOperationDisabled,
            },
            {
                label: 'Добавить коэффициент',
                action: () => {
                    if (!rowHasAcceptedCost) {
                        addRatioHandler({ strategy: null });
                    }
                },
                ...(rowHasAcceptedCost && {
                    menu: [
                        {
                            label: 'Дублирование расценки с расширенным составом коэффициентов',
                            action: () => {
                                addRatioHandler({
                                    strategy: AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients,
                                });
                            },
                        },
                        {
                            label: 'Корректировка исходной расценки',
                            action: () => {
                                addRatioHandler({
                                    strategy: AddRatioToActKc2StrategyEnum.CorrectOriginal,
                                });
                            },
                        },
                    ],
                }),
                groupId: 1,
            },
            {
                label: 'Добавить МТР',
                action: () => {
                    addMtrHandler();
                },
                groupId: 1,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: () => {
                    editNoteHandler();
                },
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                      disabled: !canRemoveRow,
                  },
        ],
        [
            addMtrHandler,
            addRatioHandler,
            canExcludeRow,
            canRemoveRow,
            editNoteHandler,
            editOperationDisabled,
            editUerHandler,
            isOpenMoveVolumesBetweenSeasonsForUerDisabled,
            onOpenMoveVolumesBetweenSeasonsForUer,
            rowHasAcceptedCost,
            singleDeleteHandler,
        ],
    );

    const coefficientMenu = useMemo(
        () => [
            {
                label: 'Редактировать',
                groupId: 1,
                action: () => {
                    editRatioHandler();
                },
                disabled: editOperationDisabled,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: () => {
                    editNoteHandler();
                },
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      disabled: !canRemoveRow,
                      groupId: 2,
                  },
        ],
        [canExcludeRow, canRemoveRow, editNoteHandler, editOperationDisabled, editRatioHandler, singleDeleteHandler],
    );

    const matchItemMenu = useMemo(
        () => [
            {
                label: 'Сопоставить с Прейскурантом МТР',
                action: () => matchMtrDialogHandler(),
            },
        ],
        [matchMtrDialogHandler],
    );

    const mtrMenu = useMemo(
        () => [
            ...(isDuplicate && rowData.status === WorkOrderPriceStatusEnum.Draft ? matchItemMenu : []),
            {
                label: 'Редактировать',
                groupId: 1,
                action: () => {
                    editMtrHandler();
                },
                disabled: editOperationDisabled,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: () => {
                    editNoteHandler();
                },
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      disabled: !canRemoveRow,
                      groupId: 2,
                  },
        ],
        [
            isDuplicate,
            rowData.status,
            matchItemMenu,
            editOperationDisabled,
            canExcludeRow,
            canRemoveRow,
            editMtrHandler,
            editNoteHandler,
            singleDeleteHandler,
        ],
    );

    const otherMenu = useMemo(
        () => [
            {
                label: editOperationDisabled ? 'Просмотреть' : 'Редактировать',
                groupId: 1,
                action: () => {
                    editOtherHandler(editOperationDisabled);
                },
            },
            {
                label: 'Изменить распределение',
                action: () => {
                    editOtherDistributionHandler();
                },
                groupId: 1,
            },
            {
                label: 'Редактировать примечание',
                groupId: 1,
                action: () => {
                    editNoteHandler();
                },
            },
            canExcludeRow
                ? {
                      label: 'Исключить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                  }
                : {
                      label: 'Удалить',
                      action: () => {
                          singleDeleteHandler();
                      },
                      groupId: 2,
                      disabled: !canRemoveRow,
                  },
        ],
        [
            canExcludeRow,
            canRemoveRow,
            editNoteHandler,
            editOperationDisabled,
            editOtherDistributionHandler,
            editOtherHandler,
            singleDeleteHandler,
        ],
    );

    const menuByRowType: { [key: string]: any } = useMemo(
        () => ({
            [WorkOrderPriceTypeEnum.Uer]: uerMenu,
            [WorkOrderPriceTypeEnum.Coefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.ConstraintCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.TechPartCoefficient]: coefficientMenu,
            [WorkOrderPriceTypeEnum.Mtr]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrContractorMaterial]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: mtrMenu,
            [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: mtrMenu,
            OTHERS: otherMenu,
        }),
        [coefficientMenu, mtrMenu, otherMenu, uerMenu],
    );

    const groups = useMemo(
        () => [
            {
                id: 1,
            },
            {
                id: 2,
            },
        ],
        [],
    );

    return {
        items: menuByRowType[rowData.type],
        groups,
    };
};
